/// <reference types="./types/assets" />

// Types
export type * from "./types/app";
export type * from "./types/config";
export type * from "./types/compliance";
export type * from "./types/capabilityAccess";
export {
    AGREEMENT_STAGE_LABEL,
    capabilityAccessActionLabel,
    capabilityGrantActionFor,
    isCapabilityGrantAccessAction
} from "./types/capabilityAccess";
export type * from "./types/orgAuth";
export type * from "./types/issuerClient";
export type * from "./types/onboardingApplication";
export type * from "./types/operatingContext";
export type * from "./types/protocol";
export type * from "./types/session";
export type * from "./types/transaction";
export type * from "./types/vault";
export type * from "./types/yaas";
export type * from "./types/yaasPositions";
export type * from "./types/orgMembers";
export * from "./types/orgAccessContracts";
export type * from "./wallet/types";
export * from "./yaas/policyCatalog";
export * from "./yaas/squadsMultisig";
export * from "./yaas/positionSourceDetection";
export * from "./yaas/lpProtocols";

// Analytics
export { capturePageview, hashForAnalytics, initAnalytics } from "./analytics";
export { PageviewTracker } from "./analytics";

// App
export * from "./app";
export { createYaasApp, YAAS_QUERY_DEFAULTS } from "./yaas-app";

// Wallet
export * from "./wallet";

// Stores
export { useWalletStore } from "./stores/walletStore";
export * from "./tokens/usdv";
export { withWalletBalanceMutationLock } from "./wallet/utils/balanceMutationLock";
export type { TokenBalanceDelta, TokenBalanceKey, TokenBalances } from "./wallet/types/balances";
export { useSessionStore } from "./stores/useSessionStore";
export { useVaultStore } from "./stores/useVaultStore";
export { useOrgAuthStore } from "./stores/useOrgAuthStore";

// Hooks
export * from "./hooks";
export { useOrgAuth } from "./hooks/auth";
export type { UseOrgAuthReturn } from "./hooks/auth";

// Services
export type { OAuthConfig } from "./services/orgAuthService";
export {
    ORG_AUTH_DISTRIBUTOR_CONSOLE_TERMS,
    setOrgAuthConsoleTerms,
    type OrgAuthConsoleTerms
} from "./services/orgAuthMessages";
export {
    orgAuthFetch,
    setOrgAuthFetchInterceptor,
    type OrgAuthFetchOptions,
    type OrgAuthFetchInterceptor
} from "./services/orgAuthFetch";
export {
    cancelMemberInvitation,
    fetchOrgMembers,
    inviteMember,
    resetMemberPassword,
    regenerateMemberClaim,
    fixMemberInvite,
    patchMember,
    changeOwnPassword,
    signOutAllSessions
} from "./services/orgMembers";
export { ApiResponseError, assertApiSuccess, readApiJson } from "./services/apiResponse";
export { invalidateQueriesAfterMutation } from "./hooks/api/invalidateQueriesAfterMutation";
export type { QueryRefreshErrorReporter } from "./hooks/api/invalidateQueriesAfterMutation";

// Utils
export {
    generateCodeVerifier,
    generateCodeChallenge,
    generateNonce,
    generateOAuthState
} from "@solomon-labs/oauth/pkce";

// Utils
export * from "./utils";

// Components
export * from "./components";
export { CapabilityAccessStatus } from "./components/CapabilityAccessStatus";

// Icons
export type * from "./assets/icons";

// Transaction types (values, not just types)
export { INITIAL_TRANSACTION_STATE } from "./types/transaction";

// Errors
export * from "./errors";

// Services
export { WebSocketManager } from "./services/websocket";
export type { SignatureNotification, AccountNotification } from "./services/websocket";

// Re-export enums (not just types)
export { TokenType } from "./types/session";
export {
    isProgramDistributionPeriodUnit,
    ParticipantAccountStatus,
    PROGRAM_DISTRIBUTION_PERIOD_UNITS,
    SurfaceCategorySlug,
    SurfaceNamingStrategy,
    SurfaceTypeSlug
} from "./types/yaas";
export { YaasPositionStatus, WalletVerificationMethod } from "./types/yaasPositions";
export { CLIENT_OPERATING_CONTEXT_HEADER } from "./types/operatingContext";

// Widget structural types (Phase 1D refactor) — UseSwapReturn /
// SwapSnapshot / SwapMode / SwapView / CtaFeedback /
// TokenSelectorProps. The shared widgets (BuyWidget, TradeWidget,
// LegacyTradeWidget) consume the orchestrator-resolved swap state
// via these types. apps/main's production `useSwap` returns a value
// structurally assignable to `UseSwapReturn` (with `quote` narrowed
// to JupiterUltraQuoteResponse).
export type * from "./widgets/types";

// Composite widgets — phase 1D/E. BuyWidget + TradeWidget hoisted
// here so external (sim-only) consumers can render them directly.
// LegacyTradeWidget remains in apps/main pending further DI work
// for the stake forms' data hooks (Phase 1F).
export { BuyWidget } from "./widgets/BuyWidget";
export { TradeWidget } from "./widgets/TradeWidget";
export { TransactionCTA } from "./widgets/TransactionCTA";
export * from "./widgets/usdv-migration";
// Enable YaaS — the real "Enable Rewards" form (YaasPositionForm) +
// its drop-in sim driver, hoisted from apps/main for external consumers.
export { YaasPositionForm } from "./widgets/yaas/YaasPositionForm";
export { YaasEnableRewardsSim } from "./widgets/yaas/YaasEnableRewardsSim";
export type { YaasEnableRewardsSimProps } from "./widgets/yaas/YaasEnableRewardsSim";
export {
    SwapContent,
    SwapContentImpl,
    getSwapCtaLabel,
    getSwapSuccessLabel,
    getSwapUsdVLabelAmount
} from "./widgets/swap/SwapContent";
export type { SwapCtaLabelInputs, SwapSuccessLabelInputs, SwapUsdVLabelAmountInputs } from "./widgets/swap/SwapContent";
